import { useEffect, useRef, useState } from "react";
import storage from "/src/utils/storage";
import "./ReviewTimer.css";

const storageLocal = storage("local");
const POSITION_KEY = "review_timer_position";
const EDGE_MARGIN = 8;

function formatDuration(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (value) => String(value).padStart(2, "0");
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

function readSavedPosition() {
    try {
        // storage.get already JSON-parses objects.
        const saved = storageLocal.get(POSITION_KEY);
        if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) return saved;
    } catch (e) {
        // no saved position (or storage unavailable): use the default one
    }
    return null;
}

// Keeps the pill fully inside the window, whatever the saved position or window size.
function clampToViewport(position, element) {
    const width = element ? element.offsetWidth : 0;
    const height = element ? element.offsetHeight : 0;
    return {
        x: Math.min(Math.max(EDGE_MARGIN, position.x), Math.max(EDGE_MARGIN, window.innerWidth - width - EDGE_MARGIN)),
        y: Math.min(Math.max(EDGE_MARGIN, position.y), Math.max(EDGE_MARGIN, window.innerHeight - height - EDGE_MARGIN)),
    };
}

// Discreet, draggable pill showing how long the current user has been on the
// conversation (raw time since page entry: the same clock the backend uses to
// record the review duration). Its position is remembered between visits.
function ReviewTimer({ startedAt }) {
    const [seconds, setSeconds] = useState(() => Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    const [position, setPosition] = useState(null);
    const [dragging, setDragging] = useState(false);
    const elementRef = useRef(null);
    const dragOffset = useRef({ x: 0, y: 0 });

    // Date.now() based (not "+1 per tick") so a throttled background tab doesn't drift.
    useEffect(() => {
        const tick = () => setSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [startedAt]);

    useEffect(() => {
        const element = elementRef.current;
        const saved = readSavedPosition();
        const fallback = {
            // Bottom-right, just left of the floating action button.
            x: window.innerWidth - (element ? element.offsetWidth : 90) - 96,
            y: window.innerHeight - (element ? element.offsetHeight : 34) - 30,
        };
        setPosition(clampToViewport(saved || fallback, element));

        const handleResize = () => setPosition((current) => (current ? clampToViewport(current, elementRef.current) : current));
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handlePointerDown = (event) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        const rect = elementRef.current.getBoundingClientRect();
        dragOffset.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
        elementRef.current.setPointerCapture(event.pointerId);
        setDragging(true);
    };

    const handlePointerMove = (event) => {
        if (!dragging) return;
        setPosition(clampToViewport({ x: event.clientX - dragOffset.current.x, y: event.clientY - dragOffset.current.y }, elementRef.current));
    };

    const handlePointerUp = (event) => {
        if (!dragging) return;
        elementRef.current.releasePointerCapture(event.pointerId);
        setDragging(false);
        if (position) {
            try {
                storageLocal.set(POSITION_KEY, position);
            } catch (e) {
                // position just isn't remembered
            }
        }
    };

    return (
        <div
            ref={elementRef}
            className={"review-timer" + (dragging ? " is-dragging" : "")}
            style={position ? { left: position.x, top: position.y } : { visibility: "hidden" }}
            title="Time spent on this conversation. It is recorded once you make a change. Drag to move."
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
        >
            <span className="review-timer__dot" />
            <i className="far fa-clock" aria-hidden="true"></i>
            <span className="review-timer__value">{formatDuration(seconds)}</span>
        </div>
    );
}

export default ReviewTimer;
